<script setup lang="ts">
import SearchField from '@/components/filter/SearchField.vue';
import { I18nKey } from '@/utils/typings';
import { EnumerationOverviewFilterObject, ScopeFilterObject } from '../types';
import { EnumerationQuantityDto } from '@/api/generator';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import { useTranslation } from 'i18next-vue';
import { ScopeType, getTranslationPrefix } from '@/utils/translationPrefix';
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import { uniq } from 'lodash';
import { computed } from 'vue';
import { truthy } from '@/utils/helper';

const { t } = useTranslation();

const props = defineProps<{
  initialData?: EnumerationQuantityDto[];
}>();

const modelValue = defineModel<EnumerationOverviewFilterObject>({
  required: true,
});

function getFilterOptions(
  options: EnumerationQuantityDto[] | undefined,
  property: 'rtSystemCategory' | 'rtSystemSubCategory',
  usage: 'evaluationUsage' | 'setupUsage',
  scope: ScopeType,
): SelectItemTypeObject<ScopeFilterObject>[] {
  const optionStrings = uniq(
    options
      ?.flatMap((enumeration) => enumeration[usage]?.[scope]?.map((s) => s[property]))
      .filter(truthy) ?? [],
  );

  return optionStrings.map((option) => ({
    title: t(`${getTranslationPrefix(scope, property)}.${option}` as I18nKey),
    value: {
      option,
      property,
      usage,
      scope,
    },
  }));
}

const evaluationUsageOptions = computed<SelectItemTypeObject<ScopeFilterObject>[]>(() => [
  ...getFilterOptions(
    props.initialData,
    'rtSystemCategory',
    'evaluationUsage',
    'medicalSystemScope',
  ),
  ...getFilterOptions(
    props.initialData,
    'rtSystemSubCategory',
    'evaluationUsage',
    'qaEquipmentScope',
  ),
]);

const setupUsageOptions = computed<SelectItemTypeObject<ScopeFilterObject>[]>(() => [
  ...getFilterOptions(props.initialData, 'rtSystemSubCategory', 'setupUsage', 'medicalSystemScope'),
  ...getFilterOptions(props.initialData, 'rtSystemSubCategory', 'setupUsage', 'qaEquipmentScope'),
]);

function clearFilters() {
  modelValue.value = {};
}
</script>

<template>
  <v-row class="align-center mt-2 pl-2 pr-2">
    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <SearchField v-model:modelValue="modelValue.search" :placeholder="$t('Search')" />
    </v-col>

    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <!-- Task Category -->
      <Autocomplete
        v-model="modelValue.evaluationUsage"
        :label="$t('TaskCategory_other')"
        :items="evaluationUsageOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="2"
        multiple
        clearable
        chips
        emptyAsUndefined
      />
    </v-col>

    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <!-- Setup Category -->
      <Autocomplete
        v-model="modelValue.setupUsage"
        :label="$t('SetupCategory_other')"
        :items="setupUsageOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="2"
        multiple
        clearable
        chips
        emptyAsUndefined
      />
    </v-col>

    <v-col md="3" lg="1" class="d-flex align-center justify-start pa-2">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
  </v-row>
</template>
